import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "../OpsShared.css";
import "./OfficePage.css";
import { getOfficeReport, getPace } from "../../../api/opsApi";
import { fetchRecordingUrl } from "../../../api/communications";

/* Office — "no customer left behind." The screen the office opens first thing:
   every place work is falling through the cracks, grouped by type, sorted by age,
   each row actionable in place. A queue you work down until it's empty. */

// action label per leak category
const ACTION_LABEL = {
  missed_calls: "Mark done",
  unreplied_texts: "Draft reply",
  unreplied_emails: "Draft reply",
  voicemails: "Mark done",
  uncontacted_leads: "Open lead",
  stale_quotes: "Follow up",
  past_due: "Schedule",
  missed_stops: "Reschedule",
  interval_violations: "Review",
};

// The report's recording_url is the stored reference (a raw Twilio URL or an S3 key),
// which a browser cannot play from an href — the Twilio one asks for credentials, the
// key is not a URL. Play signs a playable URL for this one call, when pressed:
// GET /comms/calls/<sid>/recording-url/ (lane C).
function PlayRecording({ sid }) {
  const [src, setSrc] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | error | none
  const load = async () => {
    setState("loading");
    try {
      const url = await fetchRecordingUrl(sid);
      if (!url) { setState("none"); return; }
      setSrc(url);
      setState("ready");
    } catch {
      setState("error");
    }
  };
  if (src) return <audio className="office-row-action" controls autoPlay preload="none" src={src} />;
  if (state === "none") return <span className="ops-muted office-row-action">No recording</span>;
  return (
    <button type="button" className="ops-btn ghost office-row-action" onClick={load} disabled={state === "loading"}>
      {state === "loading" ? "Loading…" : state === "error" ? "Retry play" : "▶ Play"}
    </button>
  );
}

function ageLabel(row) {
  if (row.upcoming && row.scheduled_start) {
    const days = Math.round((new Date(row.scheduled_start).getTime() - Date.now()) / 86400000);
    return days <= 0 ? "today" : `in ${days}d`;
  }
  if (row.age_hours != null) {
    const h = row.age_hours;
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 48) return `${Math.round(h)}h`;
    return `${Math.round(h / 24)}d`;
  }
  if (row.age_days != null) return `${row.age_days}d`;
  const ts = row.at || row.sent_at || row.scheduled_start || row.start_time || row.captured_at;
  if (ts) {
    const mins = Math.max(0, Math.round((Date.now() - new Date(ts).getTime()) / 60000));
    if (mins < 60) return `${mins}m`;
    if (mins < 60 * 48) return `${Math.round(mins / 60)}h`;
    return `${Math.round(mins / 1440)}d`;
  }
  return "—";
}

function primaryLabel(row) {
  return (
    row.name || row.customer_name || row.number ||
    (row.customer_id ? `Customer ${row.customer_id}` : null) ||
    row.title || row.external_id || row.id || "—"
  );
}

function secondaryLabel(cat, row) {
  if (cat === "unreplied_texts" || cat === "unreplied_emails") return row.preview || "";
  if (cat === "voicemails") return (row.summary || row.transcript || "").slice(0, 90);
  if (cat === "past_due") return row.next_service_date ? `due ${row.next_service_date}` : "";
  if (cat === "missed_stops") return row.scheduled_start ? new Date(row.scheduled_start).toLocaleString() : "";
  if (cat === "interval_violations") {
    const when = row.scheduled_start ? new Date(row.scheduled_start).toLocaleDateString() : "";
    return `${row.reason || `${row.gap_days}d since last`}${when ? ` · ${row.upcoming ? "upcoming " : ""}${when}` : ""}`;
  }
  if (cat === "stale_quotes") return row.total ? `$${Number(row.total).toLocaleString()}` : "";
  return row.email || row.phone || "";
}

export default function OfficePage() {
  const navigate = useNavigate();
  const [windowDays, setWindowDays] = useState(7); // "this week" default; toggle to today (1)
  const [report, setReport] = useState(null);
  const [pace, setPace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyRow, setBusyRow] = useState(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getOfficeReport({ window_days: windowDays }), getPace().catch(() => ({ data: null }))])
      .then(([rep, pc]) => {
        if (cancelled) return;
        setReport(rep.data);
        setPace(pc.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err.message || "Failed to load office report");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [windowDays]);

  useEffect(() => load(), [load]);

  const handleAction = async (cat, row) => {
    const a = row.action || {};
    if (a.route) { navigate(a.route); return; }
    if (a.type === "draft_reply") { navigate("/communications"); return; }
    if (a.endpoint) {
      // Mark handled server-side (shared queue). The triage endpoint only writes when
      // {handled:true} is present — an empty body 200s and clears nothing, so the row
      // would come straight back. This is an EXPLICIT "I worked this" tap: we don't
      // auto-dial or auto-play, so it's not clearing a row on a guess.
      try {
        setBusyRow(row.id);
        const api = (await import("../../../apiClient")).default;
        await api.post(a.endpoint, { handled: true });
        load(); // refresh the queue
      } catch (e) {
        setError(e?.message || "Action failed");
      } finally { setBusyRow(null); }
    }
  };

  const categories = (report?.categories || []).filter((c) => c.available !== false);
  const totalOpen = report?.total_open ?? categories.reduce((s, c) => s + (c.count || 0), 0);
  const behindTechs = (pace?.techs || []).filter((t) => !t.on_pace);

  if (report && report.enabled === false) {
    return (
      <div className="ops-page">
        <header className="ops-head"><div><h1>Office</h1>
          <p className="ops-muted">Not enabled for this account. Turn on the office report in settings.</p></div></header>
      </div>
    );
  }

  return (
    <div className="ops-page office-page">
      <header className="ops-head">
        <div>
          <h1>Office</h1>
          <p className="ops-muted">No customer left behind — work the queue down until it's empty.</p>
        </div>
        <div className="ops-actions office-window">
          <button className={`ops-btn ${windowDays === 1 ? "" : "ghost"}`} onClick={() => setWindowDays(1)}>Today</button>
          <button className={`ops-btn ${windowDays === 7 ? "" : "ghost"}`} onClick={() => setWindowDays(7)}>This week</button>
        </div>
      </header>

      <section className="ops-stat-grid">
        <div className="ops-stat"><div className="label">Open items</div><div className="value">{loading ? "—" : totalOpen}</div></div>
        <div className="ops-stat"><div className="label">Techs behind</div><div className="value">{pace ? behindTechs.length : "—"}</div></div>
        <div className="ops-stat"><div className="label">Day elapsed</div><div className="value">{pace ? `${Math.round((pace.day_elapsed_fraction || 0) * 100)}%` : "—"}</div></div>
        <div className="ops-stat"><div className="label">Categories</div><div className="value">{loading ? "—" : categories.filter((c) => c.count > 0).length}</div></div>
      </section>

      {error && <div className="ops-error" style={{ color: "var(--color-danger)", padding: "1rem" }}>{error}</div>}

      {pace && behindTechs.length > 0 && (
        <section className="ops-card">
          <div className="ops-card-head"><h3>Behind pace <span className="office-count">{behindTechs.length}</span></h3>
            <span className="ops-muted">{Math.round((pace.day_elapsed_fraction || 0) * 100)}% through the day</span></div>
          <div className="office-pace-strip">
            {behindTechs.map((t) => (
              <div className="office-pace-chip danger" key={t.technician_id}>
                <span className="who">{t.technician_name || `Tech ${t.technician_id}`}</span>
                <span className="detail">{t.completed}/{t.total} done · {t.stops_behind} behind</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {loading ? (
        <section className="ops-card"><div style={{ padding: "1.25rem" }} className="ops-muted">Loading the queue…</div></section>
      ) : totalOpen === 0 ? (
        <section className="ops-card"><div style={{ padding: "1.5rem" }}>Nothing in the queue for this window. Nice.</div></section>
      ) : (
        categories.filter((c) => c.count > 0).map((cat) => (
          <section className="ops-card" key={cat.key}>
            <div className="ops-card-head">
              <div>
                <h3>{cat.label} <span className="office-count">{cat.count}</span></h3>
                {cat.scope_note && <div className="office-scope-note ops-muted">{cat.scope_note}</div>}
              </div>
              {cat.source === "fieldroutes" && <span className="ops-muted">FieldRoutes</span>}
            </div>
            <div className="office-rows">
              {cat.rows.map((row) => (
                <div className="office-row" key={row.id}>
                  <div className="office-row-main">
                    <div className="office-row-primary">{primaryLabel(row)}</div>
                    <div className="office-row-secondary ops-muted">{secondaryLabel(cat.key, row)}</div>
                  </div>
                  <div className="office-row-age" title="age">{ageLabel(row)}</div>
                  {cat.key === "voicemails" && (row.has_recording || row.recording_url) && row.call_sid && (
                    <PlayRecording key={row.call_sid} sid={row.call_sid} />
                  )}
                  <button
                    className="ops-btn office-row-action"
                    disabled={busyRow === row.id}
                    onClick={() => handleAction(cat.key, row)}
                  >
                    {busyRow === row.id ? "…" : (ACTION_LABEL[cat.key] || "Open")}
                  </button>
                </div>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
